import { fetchContentfulByHandle } from 'connectors/contentful';
import { Entry } from 'contentful';
import { useRouteContext } from 'contexts/route';
import { IProductPagesFields } from 'global';
import { useContentful } from 'modules/content-cms/components/context';
import { useLocale } from 'hooks/use-locale';
import { useRegion } from 'hooks/use-region';
import { StaticPDPProduct } from 'modules/product-details/types';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { isCollectionPage as _isCollectionPage } from 'utils/collection';
import { isLegacyProductPageModel } from 'utils/contentful';
import { useRootStore } from 'stores/use-root-store';
import { PetType } from 'constants/pet-type';

export interface Breadcrumbs {
  breadcrumbList: {
    element: string;
    href: string;
    as?: string;
  }[];
  isLoading: boolean;
}

/**
 * It generates the data used by the Breadcrumbs component on PDPs and CDPs.
 * @param {StaticPDPProduct} product
 */
export const useBreadcrumbs = ({
  product,
}: {
  product: StaticPDPProduct | undefined;
}): Breadcrumbs => {
  const { data: contentful } = useContentful();
  const intl = useIntl();
  const locale = useLocale();
  const region = useRegion();
  const petType = useRootStore((store) => store.petType);
  const { previousPath } = useRouteContext();

  const isPreviousPathCollectionPage = useMemo(
    () => previousPath?.includes('collection'),
    [previousPath]
  );

  const [previousPathCollectionTitle, setPreviousPathCollectionTitle] = useState<
    string | undefined
  >(undefined);

  const [data, setData] = useState<Entry<IProductPagesFields> | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const path =
          isPreviousPathCollectionPage && previousPath
            ? previousPath.split('/').slice(1, 3).join('/')
            : null;
        if (path) {
          const response = await fetchContentfulByHandle({
            handle: path,
            locale,
            region,
          });
          setData(response);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isPreviousPathCollectionPage, locale, previousPath, region]);

  useEffect(() => {
    if (!isLoading) {
      const newTitle = data?.fields.breadcrumbsCollectionTitle ?? data?.fields.headline;
      if (newTitle !== previousPathCollectionTitle) {
        setPreviousPathCollectionTitle(newTitle);
      }
    }
  }, [data, isLoading, previousPathCollectionTitle]);
  const isCollectionPage = useMemo(() => _isCollectionPage(contentful), [contentful]);

  const breadcrumbsCollectionHandle = useMemo(() => {
    if (!isCollectionPage) {
      return isPreviousPathCollectionPage && previousPath
        ? previousPath.substring(1)
        : contentful.fields.mainCollection?.fields.handle;
    } else {
      return contentful.fields.handle;
    }
  }, [contentful, isCollectionPage, isPreviousPathCollectionPage, previousPath]);

  const breadcrumbsCollectionTitle = useMemo(() => {
    if (!isCollectionPage) {
      return isPreviousPathCollectionPage && previousPath
        ? previousPathCollectionTitle
        : (contentful.fields.mainCollection?.fields.breadcrumbsCollectionTitle ??
            contentful.fields.headline);
    }
    // Fallback to headline so DE breadcrumb depth matches EN when breadcrumbsCollectionTitle is absent.
    return isLegacyProductPageModel(contentful)
      ? (contentful.fields.breadcrumbsCollectionTitle ?? contentful.fields.headline)
      : contentful.fields.headline;
  }, [
    contentful,
    isCollectionPage,
    isPreviousPathCollectionPage,
    previousPath,
    previousPathCollectionTitle,
  ]);

  const breadcrumbList = useMemo(() => {
    const breadcrumbs = [
      {
        element:
          petType === PetType.Dog
            ? intl.formatMessage({ id: 'common:dogs' })
            : intl.formatMessage({ id: 'common:cats' }),
        href: `/pages/${petType === PetType.Dog ? 'hunde' : 'katzen'}`,
      },
    ];

    if (breadcrumbsCollectionHandle && breadcrumbsCollectionTitle) {
      breadcrumbs.push({
        element: breadcrumbsCollectionTitle,
        href: `/${breadcrumbsCollectionHandle}`,
      });
    }

    if (product) {
      breadcrumbs.push({
        element: product.metadata.accentuate.mainTitle || product.title,
        href: `/${contentful.fields.handle}`,
      });
    }

    return breadcrumbs;
  }, [
    petType,
    intl,
    breadcrumbsCollectionHandle,
    breadcrumbsCollectionTitle,
    product,
    contentful.fields.handle,
  ]);

  return { breadcrumbList, isLoading };
};
